import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { getClientById } from '../services/clientsService';

export const AccountContext = createContext(null);

export const AccountProvider = ({ children }) => {
  const { accountId } = useParams();
  const { accounts, backoffice, platformAdmin, canAccessAllAccounts } =
    useAuth();
  const [clientInfo, setClientInfo] = useState(null);
  const [clientLoading, setClientLoading] = useState(Boolean(accountId));

  const membership = useMemo(() => {
    if (!accountId || !Array.isArray(accounts)) return null;
    return (
      accounts.find(
        (account) => String(account?.hubSpotId) === String(accountId)
      ) || null
    );
  }, [accounts, accountId]);

  const role =
    membership?.role || (canAccessAllAccounts ? 'admin' : null);
  const permissions = useMemo(
    () => membership?.permissions || {},
    [membership]
  );
  const isAccountAdmin = Boolean(
    membership?.role === 'admin' || canAccessAllAccounts
  );

  useEffect(() => {
    let cancelled = false;

    const loadClient = async () => {
      if (!accountId) {
        setClientInfo(null);
        setClientLoading(false);
        return;
      }

      setClientLoading(true);
      try {
        const response = await getClientById(accountId);
        if (cancelled) return;
        if (response?.total === 0) {
          setClientInfo(null);
        } else {
          setClientInfo(response?.results ?? null);
        }
      } catch (error) {
        console.error('Error fetching /clients/' + accountId + ':', error);
        if (!cancelled) {
          setClientInfo(null);
        }
      } finally {
        if (!cancelled) {
          setClientLoading(false);
        }
      }
    };

    loadClient();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const hasProduct = useCallback(
    (productId) => {
      const products = clientInfo?.products;
      if (!productId || !Array.isArray(products)) return false;
      const id = String(productId);
      return products.some((product) => {
        const value = String(product);
        return value === id || value.startsWith(id) || id.startsWith(value);
      });
    },
    [clientInfo]
  );

  const value = useMemo(
    () => ({
      accountId,
      clientInfo,
      clientLoading,
      permissions,
      role,
      backoffice,
      platformAdmin,
      isAccountAdmin,
      hasProduct,
      membership,
    }),
    [
      accountId,
      clientInfo,
      clientLoading,
      permissions,
      role,
      backoffice,
      platformAdmin,
      isAccountAdmin,
      hasProduct,
      membership,
    ]
  );

  return (
    <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
  );
};
